import { MarketplaceType } from '@/Enums';
import { useSelector } from 'react-redux';
import { userSelectors } from '@/Redux/User';
import { useMemo, useState } from 'react';
import { TreeItemType } from '@/Components/TreeView/TreeView';
import { treeViewUtils } from '@/Utils';
import { mpAccountUtils } from '@/Utils/models/MpAccount';
import { useLocalization } from '@/Hooks';

export const useMarketplaceOptions = () => {
  const {
    modalWindows: {
      mpSynchronization: {
        MarketplaceTab: { AccountSyncDateInvalid },
      },
    },
  } = useLocalization();

  const mpAccounts = useSelector(userSelectors.currentUserMpAccounts);
  const mpActiveAccounts = useSelector(userSelectors.currentUserMpAccountActiveTreeView);

  const isSyncDateValid = (accountId: number) => {
    const account = mpAccounts.find((item) => item.id === accountId);

    if (!account) return false;

    return mpAccountUtils.isAllowedSynchronizeRange(account.synchFromDate, account.marketplaceType);
  };

  const filteredMpAccounts = () => {
    const excludedMpAccounts = [MarketplaceType.Manual, MarketplaceType.Market];

    return mpActiveAccounts.filter((mpAccount) => {
      return !excludedMpAccounts?.some((a) => a === mpAccount.key);
    });
  };

  const getSelectedAccounts = () => {
    const tooltipMessage = AccountSyncDateInvalid;

    return filteredMpAccounts().map((mpAccount) => {
      const allSyncDatesValid = mpAccount?.items?.every((account) => {
        return isSyncDateValid(account.key as number);
      });

      return {
        ...mpAccount,
        disabled: !allSyncDatesValid,
        selected: allSyncDatesValid,
        tooltip: !allSyncDatesValid ? tooltipMessage : '',
        items: mpAccount.items?.map((account) => {
          const syncDateValid = isSyncDateValid(account.key as number);

          return {
            ...account,
            disabled: !syncDateValid,
            selected: syncDateValid,
            tooltip: !syncDateValid ? tooltipMessage : '',
          };
        }),
      };
    });
  };

  const mpTreeViewItems = useMemo<TreeItemType<number | string>[]>(() => {
    return getSelectedAccounts();
  }, [mpActiveAccounts]);

  const [selectedAccountIds, setSelectedAccounts] = useState<(string | number)[]>(() => {
    return treeViewUtils.getSelectedKeys(getSelectedAccounts());
  });

  const selectedAccounts = useMemo(() => {
    return selectedAccountIds.filter((id) => !isNaN(+id)).map((id) => +id);
  }, [selectedAccountIds]);

  return {
    mpTreeViewItems,
    selectedAccounts,
    setSelectedAccounts,
  };
};
